import React, { useContext, useEffect, useState, FC } from 'react';

import { useLocation, useParams } from 'react-router';
import {
  Button,
  CircularProgress,
  Dialog,
  Link,
  makeStyles
} from '@material-ui/core';
import { Provider, useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import store, { RootState } from 'src/store';
import GetCodeModal from 'src/components/Modal/GetCodeModal';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { API, Auth } from 'aws-amplify';
import { editUserCompany, editUserGrade } from 'src/store/modules/client';
import { editTemplateId, editTemplateName } from 'src/store/modules/template';
import { ReactComponent as Arrowleft } from 'src/assets/arrow-left.svg';
import EditIcon from '@material-ui/icons/Edit';
import { TEMPLATES_HTML } from 'src/constants/templatesComponent';
import { FREE_TEMPLATES, STARTER_TEMPLATES } from 'src/constants/templates';
import { AuthContext, AuthStatus } from 'src/contexts/authContext';
import GuestModal from 'src/components/Modal/GuestModal';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '56px',
    minWidth: 1400,
    display: 'flex',
    zIndex: 100,
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E8EB'
  },
  toolbar: {
    width: '100%',
    height: '56px',
    margin: 'auto',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  toolbarSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center'
  },
  toolbarLeft: {
    justifyContent: 'flex-start'
  },
  toolbarCenter: {
    justifyContent: 'center'
  },
  toolbarRight: {
    justifyContent: 'flex-end',
    gap: '12px'
  },
  titleText: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    color: '#191F28',
    letterSpacing: '0.02em',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F7F8FA'
    }
  },
  titleInput: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    color: '#191F28',
    letterSpacing: '0.02em',
    padding: '6px 12px',
    border: '1px solid #E5E8EB',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    outline: 'none',
    minWidth: '200px',
    textAlign: 'center',
    '&:focus': {
      borderColor: '#191F28',
      boxShadow: '0 0 0 3px rgba(25, 31, 40, 0.08)'
    }
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    cursor: 'pointer',
    color: '#4E5968',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: '0.02em',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    border: '1px solid #E5E8EB',
    borderRadius: '12px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: '#191F28',
      textDecoration: 'none',
      backgroundColor: '#F7F8FA',
      borderColor: '#D1D6DB'
    },
    '&:active': {
      backgroundColor: '#F2F3F5'
    }
  },
  backIcon: {
    width: '18px',
    height: '18px',
    transition: 'transform 0.2s ease',
    '$backButton:hover &': {
      transform: 'translateX(-2px)'
    }
  },

  uploadBtn: {
    minWidth: 100,
    height: 40,
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    outline: 'none',
    border: '1px solid #E5E8EB',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#191F28',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    textTransform: 'none',
    background: '#FFFFFF',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: '#F7F8FA',
      borderColor: '#D1D6DB'
    },
    '&:active': {
      background: '#F2F3F5'
    }
  },
  primaryBtn: {
    minWidth: 100,
    height: 40,
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    outline: 'none',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    textTransform: 'none',
    background: '#191F28',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: '#333D4B'
    },
    '&:active': {
      background: '#4E5968'
    }
  },
  uploadIcon: {
    width: 16,
    height: 16,
    marginLeft: 4
  },
  saveBtn: {
    height: 36,
    margin: 'auto',
    padding: '0 16px',
    outline: 'none',
    border: '1px solid #E5E8EB',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#191F28',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    background: '#FFFFFF',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#F2F3F5'
    }
  },
  exportBtn: {
    height: 36,
    margin: 'auto',
    padding: '0 16px',
    outline: 'none',
    border: '1px solid #E5E8EB',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#191F28',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    background: '#FFFFFF',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#F2F3F5'
    }
  },
  csvBtnWrapper: {
    height: 36,
    margin: 'auto',
    padding: 0,
    outline: 'none',
    border: '1px solid #E5E8EB',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#191F28',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    background: '#FFFFFF',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#F2F3F5'
    }
  },
  csvBtn: {
    width: '100%',
    height: 36,
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#191F28',
    textDecoration: 'none'
  },

  dialog: {
    top: 0,
    right: 0,
    position: 'fixed',
    width: '50%',
    margin: 0,
    height: '100%',
    maxHeight: 'max-content',
    borderRadius: '16px'
  },
  sizeIcon: {
    width: '1.5rem',
    height: '1.5rem',
    marginLeft: 12,
    border: '1px solid #E5E8EB',
    cursor: 'pointer',
    borderRadius: '999px',
    backgroundColor: '#FFFFFF',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#F7F8FA'
    }
  },
  imageGuide: {
    position: 'absolute',
    top: 20,
    left: '98%'
  }
}));
const NavTop: FC = () => {
  const templateTitle = useSelector(
    (state: RootState) => state.template.templateTitle
  );
  const templateName = useSelector(
    (state: RootState) => state.template.templateName
  );
  const userCompany = useSelector(
    (state: RootState) => state.client.userCompany
  );
  const starIndex = useSelector(
    (state: RootState) => state.imageUpload.starIndex
  );
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const bgVideo = useSelector((state: RootState) => state.imageUpload.bgVideo);
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );
  const btnTextAlign = useSelector(
    (state: RootState) => state.buttons.btnTextAlign
  );
  const btnAgain = useSelector((state: RootState) => state.buttons.btnAgain);

  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);
  const logCustom = useSelector(
    (state: RootState) => state.trackings.logCustom
  );
  const btnType = useSelector((state: RootState) => state.buttons.btnType);
  const switchDarkMode = useSelector(
    (state: RootState) => state.switchButton.switchDarkMode
  );
  const switchPosition = useSelector(
    (state: RootState) => state.switchButton.switchPosition
  );
  const switchMediaType = useSelector(
    (state: RootState) => state.switchButton.switchMediaType
  );
  const userGrade = useSelector((state: RootState) => state.client.userGrade);

  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [brazeCode, setBrazeCode] = useState<string | string[]>(
    '이 템플릿을 사용할 권한이 없습니다.'
  );
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [freeExpired, setFreeExpired] = useState(true);
  const [company, setCompany] = useState(userCompany);
  const [exportedCount, setExportedCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  // Template rename state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [templateClientName, setTemplateClientName] = useState('');

  const onClickGuestModal = () => {
    setShowModal(prev => !prev);
  };

  const handleStartRename = () => {
    if (authStatus !== AuthStatus.SignedIn || !id) return;
    setEditedName(templateName === '' ? TITLE : templateName);
    setIsEditingName(true);
  };

  const handleCancelRename = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleSaveRename = async () => {
    // 이미 수정 모드가 아니면 중복 호출 방지
    if (!isEditingName) return;

    const trimmedName = editedName.trim();
    const currentName = templateName === '' ? TITLE : templateName;

    // 빈 값이거나 ID 없으면 취소
    if (!trimmedName || !id) {
      handleCancelRename();
      return;
    }

    // 이름이 변경되지 않았으면 API 호출 없이 종료
    if (trimmedName === currentName) {
      setIsEditingName(false);
      return;
    }

    try {
      await API.put('iam-editor', `/templates/${id}/rename`, {
        body: {
          templateName: trimmedName,
          clientName: templateClientName
        }
      });
      dispatch(editTemplateName(trimmedName));
      setIsEditingName(false);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(error?.message || 'Rename failed');
      Sentry.captureException(err, { extra: { response: error?.response } });
      handleCancelRename();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Prevent special characters that could cause issues
    const lastChar = value.slice(-1);
    if (lastChar === '\\' || lastChar === '"' || lastChar === "'") {
      return;
    }
    setEditedName(value);
  };

  const FINAL_VALUES = {
    company,
    starIndex,

    bgColor,
    bgImage,
    bgVideo,
    bgOpacity,
    deeplink,
    radius,

    text,
    fontSize,
    fontWeight,
    color,
    fontFamily,
    textAlign,

    btnText,
    btnFontSize,
    btnFontWeight,
    btnColor,
    btnBgColorR,
    btnBgColorG,
    btnBgColorB,
    btnBgColorA,
    btnFontFamily,
    btnTextAlign,
    btnAgain,

    tracking,
    button1,
    button2,
    logCustom,
    btnType,
    switchDarkMode,
    switchPosition,
    switchMediaType
  };

  const TEMPLATE_TITLE = location?.pathname?.split('/')?.[2];

  const TITLE = location?.pathname
    ?.split('/')?.[2]
    ?.split('-')
    ?.join(' ')
    ?.toUpperCase();

  const { authStatus } = useContext(AuthContext);
  ChannelService.loadScript();

  useEffect(() => {
    async function onLoad() {
      try {
        if (location?.pathname?.split('/')?.[3] !== undefined) {
          const data = await API.get('iam-editor', `/templates/${id}`, '');
          dispatch(editTemplateName(data?.templateName));
          setTemplateClientName(data?.clientName || '');
        }
      } catch (error) {
        history.replace(`/editor/${TEMPLATE_TITLE}`);
      }

      try {
        const result = await API.get(
          'iam-editor',
          '/subscription/subscription-info',
          ''
        );
        dispatch(editUserCompany(result?.companyName));
        setCompany(result?.companyName);

        if (
          result.subscriptionExpireDate &&
          result.subscriptionExpireDate - Date.now() > 0 &&
          !result.subscription
        ) {
          setFreeExpired(false);
          dispatch(editUserGrade('PRO'));
        } else if (
          result.subscriptionExpireDate &&
          result.subscriptionExpireDate - Date.now() > 0 &&
          result.subscription
        ) {
          if (STARTER_TEMPLATES.includes(TITLE)) {
            setFreeExpired(false);
          } else {
            setFreeExpired(true);
          }
          dispatch(editUserGrade('STARTER'));
        } else if (
          result.freeTrialExpireDate &&
          result.freeTrialExpireDate - Date.now() > 0
        ) {
          setFreeExpired(false);
          dispatch(editUserGrade('PRO'));
        } else {
          if (FREE_TEMPLATES.includes(TITLE)) {
            setFreeExpired(false);
          } else {
            setFreeExpired(true);
          }
          setExportedCount(result.exportCount ?? 0);
          dispatch(editUserGrade('FREE'));
        }

        ChannelService.boot({
          pluginKey: process.env.REACT_APP_PLUGIN_KEY,
          memberId: result.userId,
          profile: {
            name: result.companyName,
            email: result.companyEmail
          }
        });
      } catch (error) {}
    }
    if (authStatus !== AuthStatus.SignedIn) return;
    onLoad();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  async function handleExport(event: any) {
    event.preventDefault();
    authStatus === AuthStatus.SignedIn
      ? ReactGA.event('user_export_event', {})
      : ReactGA.event('guest_export_event', {});

    if (authStatus !== AuthStatus.SignedIn) {
      onClickGuestModal();
      return;
    }
    if (freeExpired) {
      if (userGrade === 'FREE') {
        alert('Available after PRO or STARTER subscription.');
        return;
      }
      if (userGrade === 'STARTER') {
        alert('This template is only available with PRO subscription.');
      }
    } else {
      setIsExporting(true);
      try {
        if (userGrade === 'FREE') {
          if (exportedCount > 49) {
            alert('You have used up all your export count.');
            return;
          }
          const remainCount = await exportCount(exportedCount);
          setExportedCount(remainCount?.exportCount);
        }

        const returnId = await saveTemplate(id);
        if (returnId !== null) {
          dispatch(editTemplateId(returnId));
          history.push(
            `/editor/${TITLE.toLowerCase()
              .split(' ')
              .join('-')}/${returnId}`
          );
        }
        const htmlString = ReactDOMServer.renderToString(
          <Provider store={store}>{TEMPLATES_HTML[TITLE]}</Provider>
        );
        const isExport = await exportTemplate(returnId, htmlString);
        if (isExport) {
          const code = `<html>${htmlString}</html>`;
          const frontBody = code.split('<body>')[0];
          const bodyContent = `<body>${
            code.split('<body>')[1].split('</body>')[0]
          }</body>`;
          const backBody = code.split('</body>')[1];
          const CODE = [
            btoa(unescape(encodeURIComponent(frontBody))),
            bodyContent,
            btoa(unescape(encodeURIComponent(backBody)))
          ];
          setBrazeCode(CODE);
          setOpen(true);
        }
      } catch (error) {
      } finally {
        setIsExporting(false);
      }
    }
  }

  async function exportTemplate(id: string, htmlString: any) {
    try {
      return await API.post('iam-editor', '/templates/export', {
        body: {
          htmlFile: htmlString,
          client: userCompany.replaceAll(' ', '-'),
          templateId: id
        }
      });
    } catch (error) {
      const err = new Error(error?.response?.data?.message || error?.message || 'Export failed');
      Sentry.captureException(err, { extra: { status: error?.response?.status } });
      if (error?.response?.status === 403) {
        alert('Authentication failed, please login again');
      }
    }
  }

  async function exportCount(count) {
    try {
      return await API.post('iam-editor', '/iam-update', {
        body: {
          exportCount: count
        }
      });
    } catch (error) {
      const err = new Error(error?.response?.data?.message || error?.message || 'Export count failed');
      Sentry.captureException(err, { extra: { status: error?.response?.status } });
      if (error?.response?.status === 403) {
        alert('Authentication failed, please login again');
      }
    }
  }

  async function handleSave(e: any) {
    e.preventDefault();
    authStatus === AuthStatus.SignedIn
      ? ReactGA.event('user_save_event', {})
      : ReactGA.event('guest_save_event', {});

    if (authStatus !== AuthStatus.SignedIn) {
      onClickGuestModal();
      return;
    }
    try {
      const returnId = await saveTemplate(id);
      if (returnId !== null) {
        alert('Save Successful');
        history.push(
          `/editor/${TITLE.toLowerCase()
            .split(' ')
            .join('-')}/${returnId}`
        );
      }
    } catch (error) {
      const err = new Error(error?.response?.data?.message || error?.message || 'Save failed');
      Sentry.captureException(err, { extra: { status: error?.response?.status } });
      if (error?.response?.status === 403) {
        alert('Authentication failed, please login again');
      }
    }
  }

  async function saveTemplate(id: string) {
    const current = await Auth.currentUserInfo();
    setCompany(current.attributes['custom:Company']);
    if (id) {
      return API.put('iam-editor', `/templates/${id}`, {
        body: {
          customValues: FINAL_VALUES,
          templateName: templateName === '' ? TITLE : templateName,
          clientName: current.attributes['custom:Company'].replaceAll(' ', '-')
        }
      });
    }
    return API.post('iam-editor', '/templates/save', {
      body: {
        customValues: FINAL_VALUES,
        iamType: templateTitle,
        templateName: templateName === '' ? TITLE : templateName,
        clientName: current.attributes['custom:Company'].replaceAll(' ', '-')
      }
    });
  }

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <div className={`${classes.toolbarSection} ${classes.toolbarLeft}`}>
          <Link
            component={RouterLink}
            to={
              authStatus === AuthStatus.SignedIn
                ? '/workspace/draft'
                : '/workspace/templates'
            }
            className={classes.backButton}
          >
            <Arrowleft className={classes.backIcon} />
            <span>Back</span>
          </Link>
        </div>
        <div className={`${classes.toolbarSection} ${classes.toolbarCenter}`}>
          {isEditingName ? (
            <input
              type="text"
              className={classes.titleInput}
              value={editedName}
              onChange={handleNameChange}
              onKeyDown={handleNameKeyDown}
              onBlur={handleSaveRename}
              autoFocus
            />
          ) : (
            <span
              className={classes.titleText}
              onClick={handleStartRename}
              title={id ? 'Click to rename' : ''}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {templateName === '' ? TITLE : templateName}
              {id && (
                <EditIcon
                  style={{
                    width: 14,
                    height: 14,
                    color: '#8B95A1',
                    transition: 'color 0.2s ease'
                  }}
                />
              )}
            </span>
          )}
        </div>
        <div className={`${classes.toolbarSection} ${classes.toolbarRight}`}>
          <Button className={classes.uploadBtn} onClick={handleSave}>
            <span>Save</span>
          </Button>
          <Button
            onClick={handleExport}
            className={classes.primaryBtn}
            disabled={isExporting}
            style={{ opacity: isExporting ? 0.7 : 1 }}
          >
            {isExporting ? (
              <>
                <CircularProgress size={16} style={{ color: '#FFFFFF' }} />
                <span>Exporting...</span>
              </>
            ) : (
              <span>Get Code</span>
            )}
          </Button>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <GetCodeModal handleClose={handleClose} getCode={brazeCode} />
        </Dialog>
      </div>
      {showModal && <GuestModal onClickGuestModal={onClickGuestModal} />}
    </div>
  );
};

export default NavTop;
